import React, { useEffect, useState } from "react";
import axios from "axios";
import { Building2, Home, Users as UsersIcon, ClipboardList, Mail, Sliders, Wrench, CreditCard, FileDown, BookOpen } from "lucide-react";
import { useAuth, isPlatformAdmin, isPlatformOrFirmAdmin, isAnyUserAdmin } from "../contexts/AuthContext";
import MaintenanceModal from "../components/MaintenanceModal";
import RegressionScriptModal from "../components/RegressionScriptModal";
import DocumentationModal from "../components/DocumentationModal";
// 2026-02-19 (Dave) — Engine freeze decision. EngineDriftTile import
// removed; the shadow comparator is OFF and the tile is replaced with
// an "Engine: Python · single-engine mode" pill. The component file
// remains at /app/frontend/src/components/EngineDriftTile.jsx as ready
// material for the eventual Engine Flip project.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2026-02-19 (Dave) — Admin header "Date Published" label. Fetches the
// backend's process start-time (captured on module import) and formats
// it in the user's local timezone. On production this equals "when the
// current build went live" because production restarts only happen on
// a fresh publish. On preview it's whenever the hot-reload last kicked,
// so we relabel it "Preview Restarted".
function usePublishedLabel() {
  const [label, setLabel] = useState({ heading: "Date Published", value: "…" });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/build-info/published`).then((r) => {
      if (cancelled) return;
      const value = new Date(r.data.published_at).toLocaleString(undefined, {
        timeZone: "America/Chicago",
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
      setLabel({ heading: "Date Published", value });
    }).catch(() => {
      if (!cancelled) setLabel({ heading: "Date Published", value: "—" });
    });
    return () => { cancelled = true; };
  }, []);

  return label;
}

// Slice E5n (2026-02-09) — Admin landing / index.
//
// Per-item gates:
//   • Firms  → Platform Administrator only
//   • Users  → Platform Administrator OR Firm Administrator
//   • Audit  → Platform Administrator OR Firm Administrator
// Viewers (no admin membership at all) get a muted "None" empty state.
//
// The same gates are used by the Admin dropdown in `TopBar.jsx` — this
// screen is the fallback / landing shown when the user picks "Admin"
// from the main nav without selecting a sub-item, so both surfaces must
// agree on what's visible.
// Admin grid column 1, top to bottom; every other card goes to column 2.
const LEFT_COL_KEYS = ["Firms", "Associations", "Users", "Subscriptions"];
const RIGHT_COL_KEYS = ["Settings", "Maintenance", "EmailLog", "Audit"];

export default function AdminScreen({ onScreenChange }) {
  const [scriptOpen, setScriptOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const { user } = useAuth();
  // 2026-09-27 (Dave) — Maintenance-Mode modal state. Opened by the
  // Admin → Maintenance card OR the global `ureserve:open-maintenance`
  // event (kept parallel with the Database Settings modal wiring).
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  useEffect(() => {
    const h = () => setMaintenanceOpen(true);
    window.addEventListener("ureserve:open-maintenance", h);
    return () => window.removeEventListener("ureserve:open-maintenance", h);
  }, []);
  const canFirms = isPlatformAdmin(user);
  const canUsersAudit = isPlatformOrFirmAdmin(user);
  const canUsers = isAnyUserAdmin(user); // Association Admins see only the Users tile
  const canSettings = isPlatformAdmin(user);
  const published = usePublishedLabel();
  // 2026-02-27 (Dave) — Database Dashboard is a Platform-Admin-only
  // mockup. 2026-03-06 (Dave) — Trigger + modal moved into
  // PlatformSettingsModal (renamed Database Settings) so all
  // platform-control knobs live in one place.

  const items = [
    // 2026-02-28 (Dave, v2) — grid re-tuned to a 2×4 (row-major fill)
    // so it fills most of the viewport with larger, easier-to-scan
    // cards.
    // 2026-03-06 (Dave) — Trimmed to 6 cards (3 rows × 2):
    //   • "Reports" card removed (Illustration-only mockup retired).
    //   • "Database" card folded into the Database Settings modal
    //     as an inline card so all platform-control knobs live in
    //     one place. See PlatformSettingsModal.jsx for the new
    //     Database card in the settings grid.
    //   • "Settings" renamed to "Database Settings" to make its
    //     scope clear now that it hosts the DB health panel too.
    //
    //   Column order is set by LEFT_COL_KEYS (below the imports).
    canFirms && {
      key: "Firms",
      title: "Firms",
      description:
        "Create and manage firms, their associations, users and settings.",
      icon: Building2,
    },
    canUsersAudit && {
      key: "EmailLog",
      title: "Email Log",
      description:
        "Every email sent in the last 30 days, with delivery status.",
      icon: Mail,
    },
    canUsersAudit && {
      key: "Subscriptions",
      title: "Subscriptions",
      description:
        "Payment status and next renewal for each paying firm.",
      icon: CreditCard,
    },
    canUsersAudit && {
      key: "Associations",
      title: "Associations",
      description:
        "Search every association you administer and jump to its Dashboard.",
      icon: Home,
    },
    canUsersAudit && {
      key: "Audit",
      title: "Audit",
      description:
        "Review the history of scenario, element, association and user changes.",
      icon: ClipboardList,
    },
    canUsers && {
      key: "Users",
      title: "Users",
      description:
        "Manage user accounts, roles and firm memberships.",
      icon: UsersIcon,
    },
    canSettings && {
      key: "Settings",
      title: "Platform Settings",
      description:
        "Platform-wide defaults, plus the read-only Database health panel.",
      icon: Sliders,
      action: "settings",
    },
    // 2026-09-27 (Dave) — Platform kill-switch card. Under the
    // Database Settings tile (bottom-right of the 2-col grid) so all
    // platform-control knobs cluster in the right column.
    canSettings && {
      key: "Maintenance",
      title: "Maintenance Mode",
      description:
        "Take the app offline for everyone except Platform Admins.",
      icon: Wrench,
      action: "maintenance",
    },
  ].filter(Boolean);

  return (
    <div className="panel-compact flex flex-col" data-testid="screen-admin">
      <div style={{ borderTop: "2px solid #EA6A47", marginBottom: 6 }} data-testid="admin-coral-rule" />
      <div
        className="grid grid-cols-[1fr_auto_1fr] items-center px-3 py-1.5 rounded-[3px]"
        style={{ background: "#1C4E80", color: "#fff" }}
        data-testid="admin-header"
      >
        <span className="font-display justify-self-start" style={{ fontSize: 16, fontWeight: 700 }}>Admin</span>
        <span className="font-sans text-[11px] tracking-[0.06em]" data-testid="admin-published-label">
          <span className="uppercase text-[10px] tracking-[0.12em] font-semibold mr-1.5" style={{ color: "rgba(255,255,255,0.75)" }}>
            {published.heading}:
          </span>
          {published.value}
        </span>
        <div className="justify-self-end flex items-center gap-2">
          {canSettings && (
            <button
              type="button"
              onClick={() => setDocsOpen(true)}
              className="flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-semibold rounded-[3px] transition-colors hover:bg-white/[0.12]"
              style={{ border: "1px solid rgba(255,255,255,0.55)", color: "#fff" }}
              title="View and download the uReserve technical documentation"
              data-testid="admin-view-documentation-btn"
            >
              <BookOpen size={12} /> View Documentation
            </button>
          )}
          {canSettings && (
            <button
              type="button"
              onClick={() => setScriptOpen(true)}
              className="flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-semibold rounded-[3px] transition-colors hover:bg-white/[0.12]"
              style={{ border: "1px solid rgba(255,255,255,0.55)", color: "#fff" }}
              title="View, print or download the Core Regression Test Plan (PDF or Word)"
              data-testid="admin-regression-script-btn"
            >
              <FileDown size={12} /> Regression Test Plan
            </button>
          )}
        </div>
      </div>

      {/* 2026-02-19 (Dave · engine-freeze decision) — The 14-day zero-drift
          soak completed. TypeScript engine is frozen at parity, shadow
          comparator is OFF, Python is the sole engine. Both the original
          drift tile AND the "Engine: Python · single-engine mode" pill
          were removed per Dave's request — no visible Admin surface for
          the engine choice. See /app/summary/ENGINE_FLIP_PLAN.md if the
          project is ever reactivated. */}

      {items.length === 0 ? (
        <div
          className="mt-6 text-slate italic text-[13px]"
          data-testid="admin-empty-state"
        >
          None
        </div>
      ) : (
        <div
          className="mt-4 grid gap-x-4 gap-y-8"
          // 2026-03-05 (Dave) — Cards shrunk ~33% in height (padding
          // trimmed + `1fr` row-stretch dropped) and vertical row-gap
          // stepped up (`gap-y-6`) to breathe more between rows and
          // leave headroom for future Admin additions below.
          //
          // 2026-03-06 (Dave, v2) — Grid is now 3 rows × 2 (Reports
          // + Database cards retired). Cards revert to their
          // natural height (no `flex-1` / `grid-auto-rows: 1fr`)
          // and use `py-4` padding — ~33 % shorter than the
          // stretched fill layout — so there's clear headroom
          // underneath for future card additions. Row-gap stays
          // generous (`gap-y-8`) so rows feel airy at this
          // compressed height.
          style={{
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          }}
          data-testid="admin-item-grid"
        >
          {[
            items.filter((it) => LEFT_COL_KEYS.includes(it.key))
              .sort((a, b) => LEFT_COL_KEYS.indexOf(a.key) - LEFT_COL_KEYS.indexOf(b.key)),
            items.filter((it) => !LEFT_COL_KEYS.includes(it.key))
              .sort((a, b) => RIGHT_COL_KEYS.indexOf(a.key) - RIGHT_COL_KEYS.indexOf(b.key)),
          ].map((col, ci) => (
          <div key={ci} className="flex flex-col gap-y-8" data-testid={`admin-col-${ci + 1}`}>
          {col.map((it) => {
            const Icon = it.icon;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  if (it.action === "settings") {
                    window.dispatchEvent(
                      new Event("ureserve:open-platform-settings"),
                    );
                  } else if (it.action === "maintenance") {
                    setMaintenanceOpen(true);
                  } else {
                    onScreenChange(it.key);
                  }
                }}
                data-testid={`admin-card-${it.key.toLowerCase()}`}
                className="text-left border border-navy bg-white hover:bg-navy-wash transition-colors px-6 py-4 rounded-[3px] group flex flex-col justify-center min-w-0"
              >
                <div className="flex items-center gap-3">
                  <Icon size={22} className="text-navy" strokeWidth={1.8} />
                  <span className="font-display text-[19px] text-navy font-bold">
                    {it.title}
                  </span>
                </div>
                <div className="mt-1.5 text-[13px] text-slate leading-snug truncate" title={it.description}>
                  {it.description}
                </div>
              </button>
            );
          })}
          </div>
          ))}
        </div>
      )}
      <MaintenanceModal
        open={maintenanceOpen}
        onClose={() => setMaintenanceOpen(false)}
      />
      {scriptOpen && <RegressionScriptModal onClose={() => setScriptOpen(false)} />}
      {docsOpen && <DocumentationModal onClose={() => setDocsOpen(false)} />}
    </div>
  );
}
